"use client";

import React from "react";
import { motion, useReducedMotion } from "framer-motion";
import { Activity, TriangleAlert as AlertTriangle, ArrowRight, CircleCheck as CheckCircle2, Droplets, Layers, PhoneCall, Pipette, Search, ShieldCheck, Waves, Wrench } from 'lucide-react';
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardDescription, CardFooter, CardHeader, CardTitle } from "@/components/ui/card";
import { Link } from "@/components/common/Link";
const leakCategories = [{
  id: "pipe-leaks",
  title: "Burst & Pinhole Pipe Leaks",
  badge: "Most Common",
  description: "Copper, PEX, and galvanized pipe bursts, hairline cracks, and corrosion leaks inside walls and ceilings.",
  symptoms: "Sudden water pressure loss, drywall discoloration, sound of running water.",
  urgency: "Immediate Dispatch",
  iconName: "pipette"
}, {
  id: "slab-leaks",
  title: "Foundation & Slab Leaks",
  badge: "Structural Risk",
  description: "Pressurized water lines leaking beneath concrete subfloors and home foundations in Jonesboro.",
  symptoms: "Warm spots on floors, damp carpets, rising water meter with no faucets running.",
  urgency: "Urgent Diagnostic",
  iconName: "layers"
}, {
  id: "fixture-leaks",
  title: "Fixture & Appliance Leaks",
  badge: "Daily Wear",
  description: "Persistent leaks from water heaters, toilets, faucets, garbage disposals, and washing machine supply lines.",
  symptoms: "Pooling water under cabinets, constant toilet cycling, cabinet swelling.",
  urgency: "Same-Day Service",
  iconName: "wrench"
}, {
  id: "main-line",
  title: "Main Water Line Leaks",
  badge: "High Volume",
  description: "Underground supply line breaks between the city water meter and your building's primary shutoff.",
  symptoms: "Soggy patches in yard, low home water pressure, unusually high water bills.",
  urgency: "Rapid Excavation",
  iconName: "waves"
}, {
  id: "sewer-leaks",
  title: "Sewer & Drain Leaks",
  badge: "Biohazard",
  description: "Cracked waste stacks, misaligned sewer laterals, and tree-root-compromised drain lines.",
  symptoms: "Foul sewer odors, slow multiple drains, sinkholes or sewage back-ups.",
  urgency: "Emergency Isolation",
  iconName: "alert"
}, {
  id: "hidden-leaks",
  title: "Hidden & Concealed Leaks",
  badge: "Acoustic Detection",
  description: "Unseen leaks tracked using electronic sound amplifiers, thermal imaging, and tracer gas tech.",
  symptoms: "Musty humidity odors, hidden mold growth, unexplained water usage surges.",
  urgency: "Non-Invasive Scan",
  iconName: "search"
}];
const detectionSteps = [{
  step: "01",
  title: "Acoustic & Thermal Scan",
  desc: "Non-destructive pinpointing without unnecessary wall or floor demo."
}, {
  step: "02",
  title: "Pressure & Flow Isolation",
  desc: "Systematic line tests to confirm exact breach location and PSI rating."
}, {
  step: "03",
  title: "Permanent Code Repair",
  desc: "Long-term pipe segment replacements, couplings, and pressure restorations."
}];
export default function LeakTypes() {
  const shouldReduceMotion = useReducedMotion();
  const getIcon = (name: string) => {
    switch (name) {
      case "pipette":
        return <Pipette className="size-6 text-primary" />;
      case "layers":
        return <Layers className="size-6 text-primary" />;
      case "wrench":
        return <Wrench className="size-6 text-primary" />;
      case "waves":
        return <Waves className="size-6 text-primary" />;
      case "alert":
        return <AlertTriangle className="size-6 text-destructive" />;
      case "search":
        return <Search className="size-6 text-primary" />;
      default:
        return <Droplets className="size-6 text-primary" />;
    }
  };
  const containerVariants = {
    hidden: {
      opacity: 0
    },
    show: {
      opacity: 1,
      transition: {
        staggerChildren: shouldReduceMotion ? 0 : 0.1
      }
    }
  };
  const itemVariants = {
    hidden: {
      opacity: 0,
      y: shouldReduceMotion ? 0 : 16
    },
    show: {
      opacity: 1,
      y: 0,
      transition: {
        duration: 0.45,
        ease: "easeOut"
      }
    }
  };
  return <section id="leaktypesrepaired" data-nav="light" className="relative bg-background py-16 md:py-24">
      <div className="container mx-auto px-4 sm:px-6 lg:px-8">
        {/* Section Header */}
        <div className="mx-auto max-w-3xl text-center">
          <Badge variant="secondary" className="mb-3 px-3 py-1 font-semibold">
            <ShieldCheck className="size-3.5 mr-1 text-primary" />
            Full-Spectrum Leak Solutions
          </Badge>
          <h2 className="font-default text-3xl font-bold tracking-tight text-foreground sm:text-4xl md:text-5xl">
            Types of Water Leaks We Detect & Repair
          </h2>
          <p className="mt-4 text-base text-muted-foreground sm:text-lg">
            From subtle pinholes behind drywall to structural slab breaches, our Jonesboro emergency plumbers locate and fix hidden water leaks quickly to stop property damage.
          </p>
        </div>

        {/* Cards Grid */}
        <motion.div variants={containerVariants} initial="hidden" whileInView="show" viewport={{
        once: true,
        amount: 0.1
      }} className="mt-12 grid grid-cols-1 gap-6 sm:grid-cols-2 lg:grid-cols-3">
          {leakCategories.map((leak, index) => <motion.div key={leak.id} data-index={index} variants={itemVariants} className="h-full">
              <Card className="flex h-full flex-col justify-between border-border bg-card shadow-sm transition-[border-color,box-shadow] duration-200 hover:border-primary/40 hover:shadow-md">
                <div>
                  <CardHeader className="space-y-3 pb-3">
                    <div className="flex items-center justify-between">
                      <div className="flex size-12 items-center justify-center rounded-lg bg-secondary text-secondary-foreground">
                        {getIcon(leak.iconName)}
                      </div>
                      <Badge variant={leak.badge === "Biohazard" ? "destructive" : "secondary"} className="text-xs font-medium">
                        {leak.badge}
                      </Badge>
                    </div>
                    <CardTitle className="text-xl font-bold text-card-foreground">
                      {leak.title}
                    </CardTitle>
                    <CardDescription className="text-sm leading-relaxed text-muted-foreground">
                      {leak.description}
                    </CardDescription>
                  </CardHeader>

                  <CardContent className="space-y-3 pt-2">
                    <div className="rounded-md border border-border bg-background p-3">
                      <p className="text-xs font-semibold text-foreground uppercase tracking-wide">
                        Common Indicators
                      </p>
                      <p className="mt-1 text-xs text-muted-foreground">
                        {leak.symptoms}
                      </p>
                    </div>
                    <div className="flex items-center gap-2 text-xs font-medium text-foreground">
                      <Activity className="size-4 text-accent" />
                      <span>Action: </span>
                      <span className="font-semibold text-primary">
                        {leak.urgency}
                      </span>
                    </div>
                  </CardContent>
                </div>

                <CardFooter className="pt-2">
                  <Button asChild variant="outline" className="w-full justify-between hover:bg-primary hover:text-primary-foreground">
                    <Link to="/problems/">
                      <span>Service details</span>
                      <ArrowRight className="size-4" />
                    </Link>
                  </Button>
                </CardFooter>
              </Card>
            </motion.div>)}
        </motion.div>

        {/* Process Bar / Secondary Detail */}
        <div className="mt-16 rounded-2xl border border-border bg-secondary/40 p-6 sm:p-8 lg:p-10">
          <div className="grid grid-cols-1 gap-8 lg:grid-cols-3 lg:gap-12">
            {detectionSteps.map((step, idx) => <div key={step.step} data-index={idx} className="flex gap-4">
                <span className="font-mono text-2xl font-bold text-primary sm:text-3xl">
                  {step.step}
                </span>
                <div className="space-y-1">
                  <h3 className="text-base font-semibold text-foreground">
                    {step.title}
                  </h3>
                  <p className="text-sm text-muted-foreground">
                    {step.desc}
                  </p>
                </div>
              </div>)}
          </div>

          {/* Direct CTA Row */}
          <div className="mt-8 flex flex-col items-center justify-between gap-4 border-t border-border pt-6 sm:flex-row">
            <div className="flex items-center gap-2">
              <CheckCircle2 className="size-5 text-chart-4" />
              <span className="text-sm font-medium text-foreground">
                24/7 Rapid Emergency Response in Jonesboro & Clayton County
              </span>
            </div>

            <div className="flex w-full flex-col gap-3 sm:w-auto sm:flex-row">
              <Button asChild variant="outline" className="w-full sm:w-auto">
                <Link to="/services/">
                  All Repair Services
                </Link>
              </Button>
              <Button asChild className="w-full bg-primary text-primary-foreground shadow-sm hover:bg-primary/90 sm:w-auto">
                <a href="tel:4708479076">
                  <PhoneCall className="mr-2 size-4" />
                  Call For Immediate Leak Help
                </a>
              </Button>
            </div>
          </div>
        </div>
      </div>
    </section>;
}